import { useEffect, useRef, useState } from "react";

const DRAG_CLOSE_THRESHOLD = 100;
const DRAG_CLOSE_TRANSITION = "transform 300ms cubic-bezier(0.32, 0.72, 0, 1)";

export function useDrawerInteraction(isOpen: boolean, onOpenChange: (open: boolean) => void) {
	const touchStartY = useRef<number | null>(null);
	const panelRef = useRef<HTMLDivElement>(null);
	const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const [isDragging, setIsDragging] = useState(false);

	useEffect(() => {
		if (!isOpen && closeTimeoutRef.current) {
			clearTimeout(closeTimeoutRef.current);
			closeTimeoutRef.current = null;
		}
		return () => {
			if (closeTimeoutRef.current) {
				clearTimeout(closeTimeoutRef.current);
			}
		};
	}, [isOpen]);

	const handleDragStart = (clientY: number) => {
		touchStartY.current = clientY;
		setIsDragging(true);
	};

	const handleDragMove = (clientY: number) => {
		if (touchStartY.current === null || !panelRef.current) {
			return;
		}

		const dragDiff = clientY - touchStartY.current;

		if (dragDiff > 0) {
			panelRef.current.style.transition = "none";
			panelRef.current.style.transform = `translateY(${dragDiff}px)`;
		}
	};

	const handleDragEnd = (clientY: number) => {
		if (touchStartY.current === null || !panelRef.current) {
			return;
		}

		const dragDiff = clientY - touchStartY.current;

		if (dragDiff > DRAG_CLOSE_THRESHOLD) {
			panelRef.current.style.transition = DRAG_CLOSE_TRANSITION;
			panelRef.current.style.transform = "translateY(100%)";
			closeTimeoutRef.current = setTimeout(() => {
				onOpenChange(false);
			}, 300);
		} else {
			panelRef.current.style.transition = DRAG_CLOSE_TRANSITION;
			panelRef.current.style.transform = "";
		}

		touchStartY.current = null;
		setIsDragging(false);
	};

	const handleTouchStart = (e: React.TouchEvent) => {
		handleDragStart(e.touches[0]?.clientY ?? 0);
	};

	const handleTouchMove = (e: React.TouchEvent) => {
		handleDragMove(e.touches[0]?.clientY ?? 0);
	};

	const handleTouchEnd = (e: React.TouchEvent) => {
		handleDragEnd(e.changedTouches[0]?.clientY ?? 0);
	};

	const handleMouseDown = (e: React.MouseEvent) => {
		handleDragStart(e.clientY);
	};

	const handleMouseMove = (e: React.MouseEvent) => {
		if (isDragging) {
			handleDragMove(e.clientY);
		}
	};

	const handleMouseUp = (e: React.MouseEvent) => {
		if (isDragging) {
			handleDragEnd(e.clientY);
		}
	};

	const handleMouseLeave = () => {
		if (isDragging) {
			handleDragEnd(touchStartY.current ?? 0);
		}
	};

	return {
		panelRef,
		isDragging,
		handleTouchStart,
		handleTouchMove,
		handleTouchEnd,
		handleMouseDown,
		handleMouseMove,
		handleMouseUp,
		handleMouseLeave,
	};
}
